<template>
  <div class="commission-page">
    <div class="page-content">
      <div class="page-meta">
        <!--<span class="meta-city">{{ cityName || '--' }}</span>-->
        <span class="meta-team">{{ teamName || '--' }}</span>
      </div>

      <div class="hero-card">
        <h1 class="hero-title">兼职薪资规则配置</h1>
        <p class="hero-desc">
          {{ ruleConfigurationDesc }}
        </p>
      </div>

      <section
        v-for="section in sectionOrder"
        :key="section.key"
        class="salary-section"
      >
        <div class="section-header">
          <div class="section-title-wrap">
            <span class="section-bar" :class="section.barClass"></span>
            <div class="section-heading">
              <h2 class="section-title">{{ getSectionHeaderSchemeName(section) }}</h2>
              <!--<p v-if="getSectionHeaderTypeName(section)" class="section-subtitle">{{ getSectionHeaderTypeName(section) }}</p>-->
            </div>
            <van-popover
              :value="activeHelpSectionKey === section.key"
              trigger="click"
              placement="bottom"
              @input="handleHelpPopoverChange(section.key, $event)"
            >
              <div class="help-popover-content">{{ getSectionHelpText(section) }}</div>
              <template #reference>
                <button
                  type="button"
                  class="icon-btn help-btn"
                >
                  <i class="el-icon-question"></i>
                </button>
              </template>
            </van-popover>
          </div>

          <button
            v-if="!isPageReadOnly"
            type="button"
            class="add-plan-btn"
            :class="section.buttonClass"
            @click="openPlanPopup(section.key)"
          >
            <i class="el-icon-plus"></i>
            <span>添加方案</span>
          </button>
        </div>

        <div v-if="section.plans.length" class="plans-list">
          <article
            v-for="plan in section.plans"
            :key="plan.id"
            class="plan-card"
          >
            <button
              v-if="!isPageReadOnly"
              type="button"
              class="delete-plan-btn"
              @click="removePlan(section.key, plan.id)"
            >
              <i class="el-icon-delete"></i>
            </button>

            <div class="plan-header">
              <div class="plan-header-main">
                <h3 class="plan-title">{{ plan.ruleName || plan.title }}</h3>
                <span class="plan-tag">{{ plan.typeName || getTypeLabel(plan.type) }}</span>
              </div>
              <p class="plan-desc">{{ getPlanDescription(plan) }}</p>
            </div>

            <div class="plan-body">
              <template v-if="plan.type === 'fixedBase'">
                <div class="form-row compact-row">
                  <label class="field-label">薪资</label>
                  <van-field
                    :value="plan.amount"
                    class="text-input short-input"
                    placeholder="请输入"
                    type="number"
                    :min="getMoneyInputMin(plan)"
                    :max="getMoneyInputMax(plan)"
                    :readonly="isPageReadOnly"
                    @input="handlePlanInput(plan, 'amount', $event, 'money')"
                  />
                  <span class="field-unit">元</span>
                </div>
              </template>

              <template v-else-if="plan.type === 'gate'">
                <div
                  v-for="(gate, gateIndex) in plan.gates"
                  :key="gate.id"
                  class="form-row gate-row"
                >
                  <span class="field-label">达到</span>
                  <van-field
                    :value="gate.threshold"
                    class="text-input short-input"
                    placeholder="请输入"
                    type="digit"
                    :min="getStandardInputMin(plan)"
                    :max="getStandardInputMax(plan)"
                    :readonly="isPageReadOnly"
                    @input="handleGateInput(plan, gate, 'threshold', $event, 'integer')"
                    @blur="handleGateThresholdBlur(plan, gate, '门槛值')"
                  />
                  <span class="field-text">{{ plan.unitLabel || '人' }}</span>
                  <span class="field-text">{{ getPlanSettlementLabel(plan) }}</span>
                  <van-field
                    :value="gate.reward"
                    class="text-input short-input"
                    placeholder="请输入"
                    type="number"
                    :min="getMoneyInputMin(plan)"
                    :max="getMoneyInputMax(plan)"
                    :readonly="isPageReadOnly"
                    @input="handleGateInput(plan, gate, 'reward', $event, 'money')"
                  />
                  <span class="field-unit">元</span>
                  <button
                    v-if="!isPageReadOnly && plan.gates.length > 1"
                    type="button"
                    class="inline-delete-btn"
                    @click="removeGate(section.key, plan.id, gateIndex)"
                  >
                    <i class="el-icon-delete"></i>
                  </button>
                </div>
                <button v-if="!isPageReadOnly" type="button" class="add-tier-btn" @click="addGate(section.key, plan.id)">
                  <i class="el-icon-plus"></i>
                  <span>添加阶梯</span>
                </button>
              </template>

              <template v-else-if="plan.type === 'unit'">
                <div class="form-row compact-row">
                  <span class="field-label">每增加{{ plan.perCount || 1 }}{{ plan.unitLabel || '人' }}{{ getPlanSettlementLabel(plan) }}</span>
                  <van-field
                    :value="plan.reward"
                    class="text-input short-input"
                    placeholder="请输入"
                    type="number"
                    :min="getMoneyInputMin(plan)"
                    :max="getMoneyInputMax(plan)"
                    :readonly="isPageReadOnly"
                    @input="handlePlanInput(plan, 'reward', $event, 'money')"
                  />
                  <span class="field-unit">元</span>
                </div>
              </template>

              <template v-else-if="plan.type === 'performance'">
                <div class="form-row compact-row no-margin-top">
                  <span class="field-label">达到</span>
                  <van-field
                    :value="plan.threshold"
                    class="text-input short-input"
                    placeholder="请输入"
                    type="digit"
                    :min="getStandardInputMin(plan)"
                    :max="getStandardInputMax(plan)"
                    :readonly="isPageReadOnly"
                    @input="handlePlanInput(plan, 'threshold', $event, 'integer')"
                    @blur="handlePlanStandardBlur(plan, 'threshold', '门槛值')"
                  />
                  <span class="field-text">{{ plan.unitLabel || '人' }}</span>
                  <span class="field-text">{{ getPlanSettlementLabel(plan) }}</span>
                  <van-field
                    :value="plan.reward"
                    class="text-input short-input"
                    placeholder="请输入"
                    type="number"
                    :min="getMoneyInputMin(plan)"
                    :max="getMoneyInputMax(plan)"
                    :readonly="isPageReadOnly"
                    @input="handlePlanInput(plan, 'reward', $event, 'money')"
                  />
                  <span class="field-unit">元</span>
                </div>
                <div class="form-row compact-row no-margin-top">
                  <span class="field-label">未达到，每少1人扣减</span>
                  <van-field
                    :value="plan.deduction"
                    class="text-input short-input"
                    placeholder="请输入"
                    type="number"
                    :min="getMoneyInputMin(plan)"
                    :max="getMoneyInputMax(plan)"
                    :readonly="isPageReadOnly"
                    @input="handlePlanInput(plan, 'deduction', $event, 'money')"
                  />
                  <span class="field-unit">元</span>
                </div>
              </template>

              <template v-else-if="plan.type === 'standard'">
                <div class="form-row standard-row">
                  <select v-model="plan.compare" class="select-input small-select" :disabled="isPageReadOnly">
                    <option
                      v-for="option in getCompareOptions(plan)"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  <van-field
                    :value="plan.threshold"
                    class="text-input tiny-input"
                    placeholder="请输入"
                    type="digit"
                    :min="getStandardInputMin(plan)"
                    :max="getStandardInputMax(plan)"
                    :readonly="isPageReadOnly"
                    @input="handlePlanInput(plan, 'threshold', $event, 'integer')"
                    @blur="handlePlanStandardBlur(plan, 'threshold', '标准值')"
                  />
                  <span class="field-text">{{ plan.unitLabel || '' }}时</span>
                  <span class="field-text">{{ getPlanSettlementLabel(plan) }}</span>
                  <span class="field-inline-group">
                    <van-field
                      :value="plan.amount"
                      class="text-input tiny-input"
                      placeholder="请输入"
                      type="number"
                      :min="getMoneyInputMin(plan)"
                      :max="getMoneyInputMax(plan)"
                      :readonly="isPageReadOnly"
                      @input="handlePlanInput(plan, 'amount', $event, 'money')"
                    />
                    <span class="field-unit">元</span>
                  </span>
                </div>
              </template>

              <template v-else-if="plan.type === 'ladder'">
                <div
                  v-for="(tier, tierIndex) in plan.tiers"
                  :key="tier.id"
                  class="tier-block"
                >
                  <div class="form-row tier-row">
                    <span class="field-label">达到</span>
                    <van-field
                      :value="tier.start"
                      class="text-input tiny-input"
                      :class="{ 'readonly-input': tierIndex > 0 }"
                      placeholder="请输入"
                      type="digit"
                      :min="getStandardInputMin(plan)"
                      :max="getStandardInputMax(plan)"
                      :readonly="isPageReadOnly || tierIndex > 0"
                      @input="handleTierInput(plan, tierIndex, 'start', $event, 'integer')"
                      @blur="handleTierFieldBlur(plan, tierIndex, 'start', '阶梯开始值')"
                    />
                    <span class="field-text">{{ plan.unitLabel || '人' }}～</span>
                    <van-field
                      :value="tier.end"
                      class="text-input tiny-input"
                      :placeholder="tierIndex === plan.tiers.length - 1 ? '以上' : '请输入'"
                      type="digit"
                      :min="getStandardInputMin(plan)"
                      :max="getStandardInputMax(plan)"
                      :readonly="isPageReadOnly"
                      @input="handleTierInput(plan, tierIndex, 'end', $event, 'integer')"
                      @blur="handleTierEndBlur(plan, tierIndex)"
                    />
                    <span class="field-text">{{ getTierRangeTailText(plan, tier, tierIndex) }}时，{{ getTierStandardTypeText(tier.standardType) }}</span>
                    <template v-if="isTierIncrementRequired(tier.standardType)">
                      <van-field
                        :value="tier.increment"
                        class="text-input tiny-input"
                        placeholder="请输入"
                        type="digit"
                        :min="getStandardInputMin(plan)"
                        :max="getStandardInputMax(plan)"
                        :readonly="isPageReadOnly"
                        @input="handleTierInput(plan, tierIndex, 'increment', $event, 'integer')"
                        @blur="handleTierFieldBlur(plan, tierIndex, 'increment', '阶梯增量值')"
                      />
                      <span class="field-text">{{ plan.unitLabel || '人' }}</span>
                      <span class="field-label">{{ getPlanSettlementLabel(plan) }}</span>
                    <van-field
                      :value="tier.reward"
                      class="text-input tiny-input"
                      placeholder="请输入"
                      type="number"
                      :min="getMoneyInputMin(plan)"
                      :max="getMoneyInputMax(plan)"
                      :readonly="isPageReadOnly"
                      @input="handleTierInput(plan, tierIndex, 'reward', $event, 'money')"
                    />
                    <span class="field-unit">元</span>
                    <button
                      v-if="!isPageReadOnly && plan.tiers.length > 1"
                      type="button"
                      class="inline-delete-btn push-right"
                      @click="removeTier(section.key, plan.id, tierIndex)"
                    >
                      <i class="el-icon-delete"></i>
                    </button>
                    </template>
                  </div>
                  <!--<div class="form-row tier-row no-margin-top">
                  </div>-->
                </div>
                <button v-if="!isPageReadOnly" type="button" class="add-tier-btn" @click="addTier(section.key, plan.id)">
                  <i class="el-icon-plus"></i>
                  <span>添加阶梯</span>
                </button>
              </template>

            </div>
          </article>
        </div>

        <div v-else class="empty-card">
          <span>{{ section.emptyText }}</span>
        </div>
      </section>
    </div>

    <div v-if="!isPreviewMode" class="bottom-actions">
      <button type="button" class="action-btn primary-btn" @click="saveRules">保存</button>
      <button type="button" class="action-btn ghost-btn" @click="resetRules">重置</button>
    </div>

    <div v-if="popup.visible" class="popup-mask" @click.self="closePlanPopup">
      <div class="popup-panel">
        <div class="popup-body">
          <div class="popup-title">{{ popup.title }}</div>

          <div class="popup-section">
            <label class="popup-label">选择方案名称</label>
            <div class="select-wrapper">
              <select v-model="popup.form.schemeRuleKey" class="popup-select" @change="handlePopupSchemeChange">
                <option
                  v-for="option in popup.schemeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>

          <div class="popup-section">
            <label class="popup-label">考核指标</label>
            <div class="select-wrapper">
              <select v-model="popup.form.indicatorKey" class="popup-select" @change="handlePopupIndicatorChange">
                <option
                  v-for="option in popup.indicatorOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>

          <div class="popup-section">
            <label class="popup-label">选择计薪方式</label>
            <div class="radio-list">
              <label
                v-for="option in popup.salaryTypes"
                :key="option.value"
                class="radio-item"
              >
                <input v-model="popup.form.type" type="radio" :value="option.value" />
                <span class="radio-dot"></span>
                <span class="radio-text">{{ option.label }}：{{ option.desc }}</span>
              </label>
            </div>
          </div>
        </div>
        <div class="popup-footer">
          <button type="button" class="popup-submit" @click="confirmAddPlan">添加</button>
          <button type="button" class="popup-cancel" @click="closePlanPopup">取消</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Vue from 'vue';
import { Toast, Popover, Field } from 'vant';
import { handleBuryPoint, isAndroid } from '@/utils/tool.js';
import {
  reqSalaryTemplateDefaultConfig,
  reqSalaryTemplateGroupDetail,
  reqSalaryTemplateOperation,
} from '@/apis/serviceApi.js';

Vue.use(Toast);
Vue.use(Popover);
Vue.use(Field);

const COMMISSION_PAGE_EVENT = 'h5-sudaseeker_commissionPage';

const TYPE_OPTIONS = [
  {
    value: 'fixedBase',
    ruleType: 'fixedBase',
    label: '固定底薪',
    desc: '无任何任务要求，当天出勤即可以获得相应薪资',
  },
  {
    value: 'performance',
    ruleType: 'linearDeduction',
    label: '绩效倒扣',
    desc: '{indicator}到对应要求时，发放相应薪资，未达到时按单价X元进行扣减，扣减到0为止',
  },
  {
    value: 'standard',
    ruleType: 'unifiedStandard',
    label: '统一标准计薪',
    desc: '{indicator}达到对应规则，即计算相应薪资',
  },
  {
    value: 'unit',
    ruleType: 'multiple',
    label: '单位计薪',
    desc: '{indicator}每增加1{unit}，按单价X元计算薪资',
  },
  {
    value: 'gate',
    ruleType: 'thresholdBonus',
    label: '门槛计薪',
    desc: '{indicator}达到对应门槛时，获得对应门槛的薪资',
  },
  {
    value: 'ladder',
    ruleType: 'tiered',
    label: '阶梯计薪',
    desc: '{indicator}指标在对应阶梯时，按照阶梯内的规则计算薪资，最终薪资是每个阶梯结果之和',
  },
];

const DEFAULT_RULE_CONFIGURATION_DESC =
  '管理并配置地推人员的基础时薪、提成及推广异常扣减策略。请在当天兼职出勤前完成薪资规则的新增和修改，薪资方案将适用于团队下的所有成员。最终薪资=基础薪资+提成+异常扣减';

const SECTION_RULE_KEY_MAP = {
  basic: 'basicSalary',
  commission: 'commission',
  deduct: 'deduct',
};

const RULE_KEY_SECTION_MAP = {
  basicSalary: 'basic',
  commission: 'commission',
  deduct: 'deduct',
};

const RULE_TYPE_TO_PLAN_TYPE = {
  fixedBase: 'fixedBase',
  linearDeduction: 'performance',
  unifiedStandard: 'standard',
  thresholdBonus: 'gate',
  multiple: 'unit',
  tiered: 'ladder',
};

const PLAN_TYPE_TO_RULE_TYPE = {
  fixedBase: 'fixedBase',
  performance: 'linearDeduction',
  standard: 'unifiedStandard',
  unit: 'multiple',
  gate: 'thresholdBonus',
  ladder: 'tiered',
};

function createId() {
  return ''.concat(Date.now(), '_', Math.random().toString(36).slice(2, 8));
}

function createGate(defaults) {
  return {
    id: createId(),
    level: defaults && defaults.level != null ? defaults.level : null,
    threshold: defaults && defaults.threshold != null ? defaults.threshold : '',
    reward: defaults && defaults.reward != null ? defaults.reward : '',
  };
}

function createTier(defaults) {
  return {
    id: createId(),
    level: defaults && defaults.level != null ? defaults.level : null,
    start: defaults && defaults.start != null ? defaults.start : '',
    end: defaults && defaults.end != null ? defaults.end : '',
    standardType: defaults && defaults.standardType ? defaults.standardType : 'preAddUnit',
    increment: defaults && defaults.increment != null ? defaults.increment : '',
    reward: defaults && defaults.reward != null ? defaults.reward : '',
  };
}

function createPlan(section, type, schemeName, indicatorLabel, meta) {
  const ext = meta || {};
  const matchedType = TYPE_OPTIONS.find(function(option) {
    return option.value === type;
  }) || TYPE_OPTIONS[0];

  const basePlan = {
    id: createId(),
    section: section,
    schemeName: schemeName || '',
    indicatorLabel: indicatorLabel || '',
    ruleKey: ext.ruleKey || '',
    indicatorKey: ext.indicatorKey || '',
    sectionRuleKey: ext.sectionRuleKey || '',
    backendRuleType: ext.backendRuleType || PLAN_TYPE_TO_RULE_TYPE[type] || '',
    ruleName: ext.ruleName || schemeName || '',
    ruleDesc: ext.ruleDesc || '',
    indicatorDesc: ext.indicatorDesc || '',
    additiveCategory: ext.additiveCategory != null ? ext.additiveCategory : (section === 'deduct' ? 2 : 1),
    sort: ext.sort != null ? Number(ext.sort) : null,
    typeName: ext.typeName || matchedType.label,
    typeDesc: ext.typeDesc || matchedType.desc,
    unitLabel: ext.unitLabel || (indicatorLabel && indicatorLabel.indexOf('率') > -1 ? '%' : '人'),
    type: type,
    title: ext.typeName || matchedType.label,
    amount: '',
    threshold: '',
    reward: '',
    deduction: '',
    compare: 'gt',
    perCount: '1',
    gates: [],
    tiers: [],
  };

  return basePlan;
}

function createInitialSections() {
  return [
    {
      key: 'basic',
      title: '基础薪资',
      sort: 1,
      emptyText: '请添加方案',
      helpText: '配置固定底薪或有责底薪等基础薪资方案。',
      additiveCategory: 1,
      barClass: 'bar-basic',
      buttonClass: 'btn-basic',
      plans: [],
    },
    {
      key: 'commission',
      title: '提成',
      sort: 2,
      emptyText: '请添加方案',
      helpText: '配置入群人数、新客完单等提成方案。',
      additiveCategory: 1,
      barClass: 'bar-commission',
      buttonClass: 'btn-commission',
      plans: [],
    },
    {
      key: 'deduct',
      title: '异常扣减',
      sort: 3,
      emptyText: '请添加方案',
      helpText: '配置退群率等异常扣减规则。',
      additiveCategory: 2,
      barClass: 'bar-deduct',
      buttonClass: 'btn-deduct',
      plans: [],
    },
  ];
}

export default {
  name: 'CommissionPage',
  watch: {
    $route(to, from) {
      this.cityName = to.query.cityName || '--';
      this.teamName = to.query.teamName || '--';
      this.activeHelpSectionKey = '';
      if (this.popup.visible) {
        this.closePlanPopup();
      }
      const nextLoadKey = [to.query.groupId || '', to.query.cityCode || '', to.query.preview || '', to.query.calcDate || ''].join('|');
      const previousLoadKey = [from.query.groupId || '', from.query.cityCode || '', from.query.preview || '', from.query.calcDate || ''].join('|');
      if (nextLoadKey !== previousLoadKey) {
        this.loadPageData();
      }
    },
  },
  computed: {
    isPreviewMode() {
      return String(this.$route.query.preview || '') === 'true' && !!this.previewCalcDate;
    },
    isSavingCurrentRoute() {
      return this.isSubmitting && this.submittingRouteKey === this.getCurrentLoadKey();
    },
    isPageReadOnly() {
      return this.isPreviewMode || this.isSavingCurrentRoute;
    },
    previewCalcDate() {
      return this.$route.query.calcDate || '';
    },
    shouldUsePreviewCalcDate() {
      return this.isPreviewMode && !!this.previewCalcDate;
    },
  },
  data() {
    return {
      cityName: this.$route.query.cityName || '--',
      teamName: this.$route.query.teamName || '--',
      ruleConfigurationDesc: DEFAULT_RULE_CONFIGURATION_DESC,
      defaultConfigMeta: {
        compareMap: {},
        indicatorMap: {},
        ruleMap: {},
        ruleTypeMap: {},
        configurationList: [],
      },
      activeHelpSectionKey: '',
      sectionOrder: createInitialSections(),
      popup: {
        visible: false,
        category: '',
        title: '',
        placeholder: '请输入考核指标',
        schemeOptions: [],
        indicatorOptions: [],
        salaryTypes: TYPE_OPTIONS,
        form: {
          schemeRuleKey: '',
          indicatorKey: '',
          type: 'fixedBase',
        },
      },
      isAlive: true,
      isSubmitting: false,
      activeSaveToken: null,
      submittingRouteKey: '',
      latestLoadRequestId: 0,
      defaultSnapshot: null,
      isKeyboardVisible: false,
      focusLeaveTimer: null,
      initialViewportHeight: 0,
      tierEndValidationTimers: {},
    };
  },
  mounted() {
    this.bindKeyboardListeners();
  },
  created() {
    this.trackBuryPoint('show', {
      groupId: this.$route.query.groupId || '',
      cityCode: this.$route.query.cityCode || '',
      calcDate: this.$route.query.calcDate || '',
      preview: String(this.$route.query.preview || ''),
      teamName: this.teamName || '',
    });
    this.defaultSnapshot = this.serializeSections(this.sectionOrder);
    this.loadPageData();
  },
  beforeDestroy() {
    this.isAlive = false;
    this.latestLoadRequestId = 0;
    this.activeSaveToken = null;
    this.submittingRouteKey = '';
    this.isSubmitting = false;
    this.unbindKeyboardListeners();
    this.unlockPageScroll();
  },
  methods: {
    bindKeyboardListeners() {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }

      this.initialViewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;

      document.addEventListener('focusin', this.handleDocumentFocusIn, true);
      document.addEventListener('focusout', this.handleDocumentFocusOut, true);

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.handleViewportResize);
      } else {
        window.addEventListener('resize', this.handleViewportResize);
      }
    },
    unbindKeyboardListeners() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('focusin', this.handleDocumentFocusIn, true);
        document.removeEventListener('focusout', this.handleDocumentFocusOut, true);
      }

      if (typeof window !== 'undefined' && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.handleViewportResize);
      } else if (typeof window !== 'undefined') {
        window.removeEventListener('resize', this.handleViewportResize);
      }

      if (this.focusLeaveTimer) {
        clearTimeout(this.focusLeaveTimer);
        this.focusLeaveTimer = null;
      }

      Object.keys(this.tierEndValidationTimers || {}).forEach((timerKey) => {
        clearTimeout(this.tierEndValidationTimers[timerKey]);
      });
      this.tierEndValidationTimers = {};
    },
    handleViewportResize() {
      if (typeof window === 'undefined') {
        return;
      }

      const viewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;

      if (!this.initialViewportHeight) {
        this.initialViewportHeight = viewportHeight;
        return;
      }

      const activeEditableElement = this.getActiveEditableElement();
      if (!activeEditableElement && viewportHeight >= this.initialViewportHeight) {
        this.initialViewportHeight = viewportHeight;
        this.isKeyboardVisible = false;
        return;
      }

      this.isKeyboardVisible = this.initialViewportHeight - viewportHeight > 120;

      //if (this.isKeyboardVisible && activeEditableElement) {
      //  this.scrollFieldIntoView(activeEditableElement);
      //}
    },
    isEditableFieldElement(target) {
      if (!target || !target.tagName) {
        return false;
      }

      const tagName = target.tagName.toLowerCase();
      if (!['input', 'textarea', 'select'].includes(tagName)) {
        return false;
      }

      return !target.readOnly && !target.disabled;
    },
    getActiveEditableElement() {
      if (typeof document === 'undefined') {
        return null;
      }

      const activeElement = document.activeElement;
      return this.isEditableFieldElement(activeElement) ? activeElement : null;
    },
    handleDocumentFocusIn(event) {
      const target = event && event.target;
      if (!this.$el || !target || !this.$el.contains(target) || !this.isEditableFieldElement(target)) {
        return;
      }

      if (this.focusLeaveTimer) {
        clearTimeout(this.focusLeaveTimer);
        this.focusLeaveTimer = null;
      }

      this.isKeyboardVisible = true;
      this.scrollFieldIntoView(target);
    },
    handleDocumentFocusOut(event) {
      const target = event && event.target;
      if (!this.$el || !target || !this.$el.contains(target) || !this.isEditableFieldElement(target)) {
        return;
      }

      if (this.focusLeaveTimer) {
        clearTimeout(this.focusLeaveTimer);
      }

      this.focusLeaveTimer = setTimeout(() => {
        const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
        if (!activeElement || !this.$el || !this.$el.contains(activeElement) || !this.isEditableFieldElement(activeElement)) {
          this.isKeyboardVisible = false;
        }
      }, 180);
    },
    scrollFieldIntoView(target) {
      if (!target) {
        return;
      }

      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          const viewportHeight = typeof window !== 'undefined' && window.visualViewport
            ? window.visualViewport.height
            : window.innerHeight;
          const safeTopOffset = 88;
          const bottomOffset = isAndroid() ? 300 : 200
          const safeBottomOffset = this.isKeyboardVisible ? bottomOffset : 120;

          try {
            const rect = target.getBoundingClientRect();
            const overflowBottom = rect.bottom - (viewportHeight - safeBottomOffset);
            const overflowTop = safeTopOffset - rect.top;

            if (overflowBottom > 0 || overflowTop > 0) {
              const currentScrollTop = window.pageYOffset
                || document.documentElement.scrollTop
                || document.body.scrollTop
                || 0;
              const nextScrollTop = overflowBottom > 0
                ? currentScrollTop + overflowBottom + 24
                : Math.max(currentScrollTop - overflowTop - 24, 0);

              window.scrollTo({
                top: nextScrollTop,
                behavior: 'smooth',
              });
              return;
            }

            if (typeof target.scrollIntoView === 'function') {
              target.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
              });
            }
          } catch (error) {
            if (typeof target.scrollIntoView === 'function') {
              target.scrollIntoView(true);
            }
          }
        }, 260);
      });
    },
    trackBuryPoint(event, params) {
      handleBuryPoint(COMMISSION_PAGE_EVENT, event, params || {});
    },
    serializeSections(sections) {
      return JSON.stringify(sections);
    },
    restoreSections(snapshot) {
      return JSON.parse(snapshot);
    },
    getSection(sectionKey) {
      return this.sectionOrder.find(function(section) {
        return section.key === sectionKey;
      });
    },
    getCurrentLoadKey() {
      return [
        this.$route.query.groupId || '',
        this.$route.query.cityCode || '',
        this.$route.query.preview || '',
        this.$route.query.calcDate || '',
      ].join('|');
    },
    sortSectionsByService(sections) {
      const fallbackOrderMap = {
        basic: 1,
        commission: 2,
        deduct: 3,
      };
      return sections.slice().sort((left, right) => {
        const leftSort = left.sort != null ? Number(left.sort) : fallbackOrderMap[left.key];
        const rightSort = right.sort != null ? Number(right.sort) : fallbackOrderMap[right.key];
        if (leftSort !== rightSort) {
          return leftSort - rightSort;
        }
        return (fallbackOrderMap[left.key] || 99) - (fallbackOrderMap[right.key] || 99);
      });
    },
    getSectionConfigByKey(sectionKey) {
      const sectionRuleKey = this.getSectionRuleKey(sectionKey);
      return (this.defaultConfigMeta.configurationList || []).find((config) => {
        return config.ruleKey === sectionRuleKey;
      }) || null;
    },
    getSectionHelpText(section) {
      if (!section) {
        return '';
      }
      return this.getRuleDescByKey(this.getSectionRuleKey(section.key)) || section.helpText || '';
    },
    handleHelpPopoverChange(sectionKey, visible) {
      this.trackBuryPoint('helpPopover_click', {
        sectionKey: sectionKey,
        visible: visible,
      });
      this.activeHelpSectionKey = visible ? sectionKey : '';
    },
    getSectionAdditiveCategory(sectionKey) {
      const section = this.getSection(sectionKey);
      if (section && section.additiveCategory != null) {
        return Number(section.additiveCategory);
      }
      const config = this.getSectionConfigByKey(sectionKey);
      if (config && config.additiveCategory != null) {
        return Number(config.additiveCategory);
      }
      return sectionKey === 'deduct' ? 2 : 1;
    },
    getAdditiveText(additiveCategory) {
      return Number(additiveCategory) === 2 ? '扣减' : '奖励';
    },
    getPlanSettlementLabel(plan) {
      return this.getAdditiveText(plan && plan.additiveCategory != null ? plan.additiveCategory : 1);
    },
    getSectionHeaderPlan(section) {
      if (!section || !section.plans || !section.plans.length) {
        return null;
      }
      return section.plans[section.plans.length - 1];
    },
    getSectionHeaderSchemeName(section) {
      const plan = this.getSectionHeaderPlan(section);
      //return plan ? (plan.ruleName || plan.title || section.title) : section.title;
      return section.title;
    },
    getSectionHeaderTypeName(section) {
      const plan = this.getSectionHeaderPlan(section);
      return plan ? (plan.typeName || this.getTypeLabel(plan.type)) : '';
    },
    updateSection(sectionKey, nextPlans) {
      this.sectionOrder = this.sectionOrder.map(function(section) {
        if (section.key !== sectionKey) {
          return section;
        }
        return Object.assign({}, section, { plans: nextPlans });
      });
    },
    createEmptySections() {
      return this.sectionOrder.map(function(section) {
        return Object.assign({}, section, { plans: [] });
      });
    },
    showToast(message) {
      Toast({
        message: message,
        duration: 1800,
      });
    },
    getSectionRuleKey(sectionKey) {
      return SECTION_RULE_KEY_MAP[sectionKey] || sectionKey;
    },
    getSectionKeyByRuleKey(ruleKey) {
      return RULE_KEY_SECTION_MAP[ruleKey] || '';
    },
    getRuleNameByKey(ruleKey) {
      const target = this.defaultConfigMeta.ruleMap[ruleKey] || {};
      return target.ruleName || ruleKey || '方案';
    },
    getRuleDescByKey(ruleKey) {
      const target = this.defaultConfigMeta.ruleMap[ruleKey] || {};
      return target.ruleDesc || '';
    },
    getIndicatorMetaByKey(indicatorKey) {
      return this.defaultConfigMeta.indicatorMap[indicatorKey] || {};
    },
    getIndicatorLabelByKey(indicatorKey, fallbackLabel) {
      const indicatorMeta = this.getIndicatorMetaByKey(indicatorKey);
      if (indicatorMeta.indicatorName) {
        return indicatorMeta.indicatorName;
      }
      if (indicatorKey && this.defaultConfigMeta.ruleMap[indicatorKey]) {
        return this.defaultConfigMeta.ruleMap[indicatorKey].ruleName;
      }
      return fallbackLabel || '';
    },
    getIndicatorUnit(indicatorKey, indicatorLabel) {
      const indicatorMeta = this.getIndicatorMetaByKey(indicatorKey);
      if (indicatorMeta.unit) {
        return indicatorMeta.unit;
      }
      return indicatorLabel && indicatorLabel.indexOf('率') > -1 ? '%' : '人';
    },
    getIndicatorDescByKey(indicatorKey) {
      const indicatorMeta = this.getIndicatorMetaByKey(indicatorKey);
      return indicatorMeta.indicatorDesc || '';
    },
    getRuleTypeMeta(ruleType) {
      const target = this.defaultConfigMeta.ruleTypeMap[ruleType]
        || (ruleType === 'tiered' ? this.defaultConfigMeta.ruleTypeMap.tieredList : {})
        || {};
      if (target.key || target.name || target.desc) {
        return target;
      }

      const fallback = TYPE_OPTIONS.find(function(option) {
        return option.ruleType === ruleType;
      });

      if (!fallback) {
        return {};
      }

      return {
        key: ruleType,
        name: fallback.label,
        desc: fallback.desc,
      };
    },
    toPlanTypeByRuleType(ruleType) {
      return RULE_TYPE_TO_PLAN_TYPE[ruleType] || 'standard';
    },
    getPlanRuleTypeConfig(plan) {
      const schemeConfig = this.getPlanSchemeConfig(plan);
      const ruleTypeConfigMap = schemeConfig && schemeConfig.ruleTypeConfigMap ? schemeConfig.ruleTypeConfigMap : {};
      return (plan.indicatorKey && ruleTypeConfigMap[plan.indicatorKey]) || {};
    },
    getPlanSchemeConfig(plan) {
      const sectionConfig = (this.defaultConfigMeta.configurationList || []).find((config) => {
        return config.ruleKey === plan.sectionRuleKey;
      });

      return sectionConfig && sectionConfig.configurationItems
        ? sectionConfig.configurationItems.find((item) => item.ruleKey === plan.ruleKey)
        : null;
    },
    getCompareOptions(plan) {
      const allDef = (this.getPlanRuleTypeConfig(plan).allDef) || {};
      const compareList = (allDef.compareList || []).length ? allDef.compareList : ['>', '>=', '=', '<', '<='];
      return compareList.map((item) => {
        return {
          value: this.normalizeCompare(item),
          label: this.getCompareLabel(item),
        };
      });
    },
    getMoneyInputMin(plan) {
      const schemeConfig = this.getPlanSchemeConfig(plan);
      if (plan.ruleKey === 'fixedSalary' && schemeConfig && schemeConfig.min != null) {
        return Number(schemeConfig.min);
      }

      const allDef = (this.getPlanRuleTypeConfig(plan).allDef) || {};
      return allDef.min != null ? Number(allDef.min) : null;
    },
    getMoneyInputMax(plan) {
      const schemeConfig = this.getPlanSchemeConfig(plan);
      if (plan.ruleKey === 'fixedSalary' && schemeConfig && schemeConfig.max != null) {
        return Number(schemeConfig.max);
      }

      const allDef = (this.getPlanRuleTypeConfig(plan).allDef) || {};
      return allDef.max != null ? Number(allDef.max) : null;
    },
    getStandardInputMin(plan) {
      const allDef = (this.getPlanRuleTypeConfig(plan).allDef) || {};
      return allDef.stanMin != null ? Number(allDef.stanMin) : null;
    },
    getStandardInputMax(plan) {
      const allDef = (this.getPlanRuleTypeConfig(plan).allDef) || {};
      return allDef.stanMax != null ? Number(allDef.stanMax) : null;
    },
    isWithinStandardInputRange(plan, value) {
      if (!this.isIntegerValue(value)) {
        return true;
      }

      const numericValue = Number(value);
      const min = this.getStandardInputMin(plan);
      const max = this.getStandardInputMax(plan);
      if (min != null && numericValue < min) {
        return false;
      }
      if (max != null && numericValue > max) {
        return false;
      }
      return true;
    },
    getStandardInputRangeMessage(plan, fieldLabel) {
      const min = this.getStandardInputMin(plan);
      const max = this.getStandardInputMax(plan);

      if (min != null && max != null) {
        return ''.concat(fieldLabel, '需在').concat(min, '到').concat(max, '之间');
      }
      if (min != null) {
        return ''.concat(fieldLabel, '不能小于').concat(min);
      }
      if (max != null) {
        return ''.concat(fieldLabel, '不能大于').concat(max);
      }
      return ''.concat(fieldLabel, '超出允许范围');
    },
    clampStandardInputMaxByPlan(plan, value) {
      if (value === '' || value == null || !this.isIntegerValue(value)) {
        return value;
      }

      const numericValue = Number(value);
      const max = this.getStandardInputMax(plan);
      if (max != null && numericValue > max) {
        return String(max);
      }
      return value;
    },
    validateStandardInputRangeValue(plan, value, fieldLabel) {
      if (value === '' || !this.isIntegerValue(value)) {
        return { valid: true, message: '' };
      }

      if (this.isWithinStandardInputRange(plan, value)) {
        return { valid: true, message: '' };
      }

      return {
        valid: false,
        message: this.getStandardInputRangeMessage(plan, fieldLabel),
      };
    },
    clampMoneyInputByPlan(plan, value) {
      if (value === '' || value == null) {
        return '';
      }

      const min = this.getMoneyInputMin(plan);
      const max = this.getMoneyInputMax(plan);
      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return value;
      }

      let boundedValue = numericValue;
      if (min != null) {
        boundedValue = Math.max(boundedValue, Number(min));
      }
      if (max != null) {
        boundedValue = Math.min(boundedValue, Number(max));
      }

      return String(boundedValue);
    },
    getTierStandardTypeText(standardType) {
      if (standardType === 'perUnit' || standardType === 'preUnit' || standardType === 'perPerson') {
        return '每人';
      }
      return '每增加';
    },
    getTierRangeTailText(plan, tier, tierIndex) {
      if (tier.end) {
        return plan.unitLabel || '人';
      }
      if (tierIndex !== plan.tiers.length - 1) {
        return plan.unitLabel || '人';
      }
      //return tier.start ? '以上' : '';
      return plan.unitLabel || '人';
    },
    isTierIncrementRequired(standardType) {
      return !['perUnit', 'preUnit', 'perPerson'].includes(standardType);
    },
    normalizeDisplaySettlementValue(value) {
      if (value == null || value === '') {
        return '';
      }
      return String(Math.abs(Number(value)));
    },
    toPayloadSettlementValue(value, additiveCategory) {
      const amount = Number(value || 0);
      //return Number(additiveCategory) === 2 ? -Math.abs(amount) : Math.abs(amount);
      return Number(Math.abs(amount));
    },
    sanitizeNumberInput(value, mode) {
      const raw = value == null ? '' : String(value);
      if (!raw) {
        return '';
      }

      if (mode === 'money') {
        let sanitized = raw.replace(/[^\d.]/g, '');
        const firstDotIndex = sanitized.indexOf('.');
        if (firstDotIndex > -1) {
          sanitized = sanitized.slice(0, firstDotIndex + 1) + sanitized.slice(firstDotIndex + 1).replace(/\./g, '');
        }
        if (sanitized.startsWith('.')) {
          sanitized = '0'.concat(sanitized);
        }
        const segments = sanitized.split('.');
        const integerPart = (segments[0] || '').replace(/^0+(?=\d)/, '') || (segments.length > 1 ? '0' : '');
        if (segments.length === 1) {
          return integerPart;
        }
        return ''.concat(integerPart || '0', '.').concat((segments[1] || '').slice(0, 2));
      }

      return raw.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    },
    isIntegerValue(value) {
      return /^\d+$/.test(String(value));
    },
    isMoneyValue(value) {
      return /^\d+(\.\d{1,2})?$/.test(String(value));
    },
    isNonNegativeNumber(value) {
      return this.isIntegerValue(value) || this.isMoneyValue(value);
    },
    isPositiveNumber(value) {
      return this.isNonNegativeNumber(value) && Number(value) > 0;
    },
    handlePlanInput(plan, field, rawValue, mode) {
      if (this.isPageReadOnly) {
        return;
      }
      const sanitizedValue = this.sanitizeNumberInput(rawValue, mode);
      plan[field] = mode === 'money'
        ? this.clampMoneyInputByPlan(plan, sanitizedValue)
        : this.clampStandardInputMaxByPlan(plan, sanitizedValue);
    },
    handlePlanStandardBlur(plan, field, fieldLabel) {
      if (this.isPageReadOnly) {
        return;
      }

      const validation = this.validateStandardInputRangeValue(plan, plan[field], fieldLabel);
      if (!validation.valid) {
        plan[field] = '';
        this.showToast(validation.message);
      }
    },
    handleGateInput(plan, gate, field, rawValue, mode) {
      if (this.isPageReadOnly) {
        return;
      }
      const sanitizedValue = this.sanitizeNumberInput(rawValue, mode);
      gate[field] = mode === 'money'
        ? this.clampMoneyInputByPlan(plan, sanitizedValue)
        : this.clampStandardInputMaxByPlan(plan, sanitizedValue);
    },
    getGateValidationTimerKey(plan, gate) {
      return ''.concat(plan.id, '_gate_').concat(gate.id);
    },
    clearGateThresholdValidationTimer(plan, gate) {
      const timerKey = this.getGateValidationTimerKey(plan, gate);
      const timer = this.tierEndValidationTimers[timerKey];
      if (timer) {
        clearTimeout(timer);
        this.$delete(this.tierEndValidationTimers, timerKey);
      }
    },
    validateGateThresholdValue(plan, gate, options) {
      const setting = options || {};
      if (!gate) {
        return true;
      }

      if (gate.threshold === '') {
        return true;
      }

      const gateIndex = (plan.gates || []).findIndex(function(item) {
        return item.id === gate.id;
      });
      const thresholdValidation = this.validateGateThreshold(plan, gateIndex, gate.threshold);
      if (thresholdValidation.valid) {
        return true;
      }

      if (setting.clearInvalid !== false) {
        gate.threshold = '';
      }
      if (setting.showMessage !== false) {
        this.showToast(thresholdValidation.message);
      }
      return false;
    },
    handleGateThresholdBlur(plan, gate, fieldLabel) {
      if (this.isPageReadOnly) {
        return;
      }
      this.clearGateThresholdValidationTimer(plan, gate);
      const rangeValidation = this.validateStandardInputRangeValue(plan, gate.threshold, fieldLabel || '门槛值');
      if (!rangeValidation.valid) {
        gate.threshold = '';
        this.showToast(rangeValidation.message);
        return;
      }
      this.validateGateThresholdValue(plan, gate, {
        clearInvalid: true,
        showMessage: true,
      });
    },
    validateGateThreshold(plan, gateIndex, value) {
      if (value === '' || gateIndex < 0) {
        return { valid: true, message: '' };
      }

      if (!this.isIntegerValue(value)) {
        return { valid: true, message: '' };
      }

      const numericValue = Number(value);
      const previousGate = gateIndex > 0 ? plan.gates[gateIndex - 1] : null;
      const nextGate = gateIndex < plan.gates.length - 1 ? plan.gates[gateIndex + 1] : null;

      if (previousGate && this.isIntegerValue(previousGate.threshold) && numericValue <= Number(previousGate.threshold)) {
        return {
          valid: false,
          message: ''.concat(plan.title, '门槛值需大于上一阶梯'),
        };
      }

      if (nextGate && this.isIntegerValue(nextGate.threshold) && numericValue >= Number(nextGate.threshold)) {
        return {
          valid: false,
          message: ''.concat(plan.title, '门槛值需小于下一阶梯'),
        };
      }

      return { valid: true, message: '' };
    },
    clearFollowingTierRanges(plan, tierIndex) {
      for (let index = tierIndex + 1; index < plan.tiers.length; index += 1) {
        plan.tiers[index].start = '';
        plan.tiers[index].end = '';
      }
    },
    normalizeTierRanges(tiers) {
      return (tiers || []).map(function(tier, index) {
        if (index === 0) {
          return Object.assign({}, tier);
        }

        const previousTier = tiers[index - 1];
        const nextStart = previousTier.end === '' || previousTier.end == null
          ? ''
          : String(Number(previousTier.end) + 1);
        const shouldClearEnd = tier.end !== '' && nextStart !== '' && Number(tier.end) <= Number(nextStart);

        return Object.assign({}, tier, {
          start: nextStart,
          end: shouldClearEnd ? '' : tier.end,
        });
      });
    },
    syncFollowingTierRanges(plan, tierIndex, nextEnd) {
      this.clearFollowingTierRanges(plan, tierIndex);

      if (nextEnd == null || Number.isNaN(Number(nextEnd))) {
        this.clearFollowingTierRanges(plan, tierIndex);
        return;
      }

      const nextTier = plan.tiers[tierIndex + 1];
      if (nextTier) {
        nextTier.start = String(Number(nextEnd) + 1);
      }
    },
    getTierValidationTimerKey(plan, tierIndex) {
      return ''.concat(plan.id, '_').concat(tierIndex);
    },
    clearTierEndValidationTimer(plan, tierIndex) {
      const timerKey = this.getTierValidationTimerKey(plan, tierIndex);
      const timer = this.tierEndValidationTimers[timerKey];
      if (timer) {
        clearTimeout(timer);
        this.$delete(this.tierEndValidationTimers, timerKey);
      }
    },
    getTierEndInvalidMessage(plan, tier) {
      if (!this.isIntegerValue(tier.start)) {
        return ''.concat(plan.title, '请先填写有效的阶梯开始值');
      }

      return ''.concat(plan.title, '阶梯结束值必须大于开始值');
    },
    validateTierEnd(plan, tierIndex, options) {
      const setting = options || {};
      const tier = plan.tiers[tierIndex];
      if (!tier) {
        return true;
      }

      if (tier.end === '') {
        this.clearFollowingTierRanges(plan, tierIndex);
        return true;
      }

      if (!this.isIntegerValue(tier.end) || !this.isIntegerValue(tier.start) || Number(tier.end) <= Number(tier.start)) {
        if (setting.clearInvalid !== false) {
          tier.end = '';
        }
        this.clearFollowingTierRanges(plan, tierIndex);
        if (setting.showMessage !== false) {
          this.showToast(this.getTierEndInvalidMessage(plan, tier));
        }
        return false;
      }

      this.syncFollowingTierRanges(plan, tierIndex, Number(tier.end));
      return true;
    },
    handleTierEndBlur(plan, tierIndex) {
      if (this.isPageReadOnly) {
        return;
      }
      this.clearTierEndValidationTimer(plan, tierIndex);
      const tier = plan.tiers[tierIndex];
      if (!tier) {
        return;
      }
      const rangeValidation = this.validateStandardInputRangeValue(plan, tier.end, '阶梯结束值');
      if (!rangeValidation.valid) {
        tier.end = '';
        this.clearFollowingTierRanges(plan, tierIndex);
        this.showToast(rangeValidation.message);
        return;
      }
      this.validateTierEnd(plan, tierIndex, {
        clearInvalid: true,
        showMessage: true,
      });
    },
    handleTierInput(plan, tierIndex, field, rawValue, mode) {
      if (this.isPageReadOnly) {
        return;
      }
      const tier = plan.tiers[tierIndex];
      if (!tier) {
        return;
      }

      if (field === 'start' && tierIndex > 0) {
        return;
      }

      const nextValue = this.sanitizeNumberInput(rawValue, mode);
      tier[field] = mode === 'money'
        ? this.clampMoneyInputByPlan(plan, nextValue)
        : this.clampStandardInputMaxByPlan(plan, nextValue);

      if (field !== 'end') {
        return;
      }

      if (!this.isIntegerValue(nextValue)) {
        this.clearFollowingTierRanges(plan, tierIndex);
        return;
      }

      if (!this.isIntegerValue(tier.start) || Number(nextValue) <= Number(tier.start)) {
        this.clearFollowingTierRanges(plan, tierIndex);
        return;
      }

      this.syncFollowingTierRanges(plan, tierIndex, Number(nextValue));
    },
    handleTierFieldBlur(plan, tierIndex, field, fieldLabel) {
      if (this.isPageReadOnly) {
        return;
      }

      const tier = plan.tiers[tierIndex];
      if (!tier) {
        return;
      }

      const validation = this.validateStandardInputRangeValue(plan, tier[field], fieldLabel);
      if (!validation.valid) {
        tier[field] = '';
        if (field === 'start' || field === 'increment') {
          this.clearFollowingTierRanges(plan, tierIndex);
        }
        this.showToast(validation.message);
      }
    },
    formatTypeDescription(type, indicatorLabel, unitLabel, value, additiveCategory) {
      const indicator = indicatorLabel || '指标';
      const unit = unitLabel || '人';
      const amountText = value || 'X';
      const settlementText = this.getAdditiveText(additiveCategory);
      const isDeduction = settlementText === '扣减';

      if (type === 'fixedBase') {
        return '无任何任务要求，当天出勤即可以获得相应薪资';
      }
      if (type === 'gate') {
        return isDeduction
          ? ''.concat(indicator, '达到对应门槛时，按对应门槛计算扣减')
          : ''.concat(indicator, '达到对应门槛时，获得对应门槛的奖励');
      }
      if (type === 'unit') {
        return ''.concat(indicator, '每增加1').concat(unit, '，按单价').concat(amountText, '元计算').concat(settlementText);
      }
      if (type === 'performance') {
        return ''.concat(indicator, '到对应要求时，发放相应薪资，未达到时按单价').concat(amountText, '元进行扣减，扣减到0为止');
      }
      if (type === 'ladder') {
        return ''.concat(indicator, '指标在对应阶梯时，按照阶梯内的规则计算').concat(settlementText, '，最终').concat(settlementText, '是每个阶梯结果之和');
      }
      if (type === 'standard') {
        return ''.concat(indicator, '达到对应规则，即计算相应').concat(settlementText);
      }
      return '';
    },
    refreshPopupSalaryTypeDescriptions() {
      const indicatorLabel = this.getIndicatorLabelByKey(this.popup.form.indicatorKey, '指标');
      const unitLabel = this.getIndicatorUnit(this.popup.form.indicatorKey, indicatorLabel);
      const additiveCategory = this.getSectionAdditiveCategory(this.popup.category);
      this.popup.salaryTypes = this.popup.salaryTypes.map((option) => {
        return Object.assign({}, option, {
          desc: this.formatTypeDescription(option.value, indicatorLabel, unitLabel, 'X', additiveCategory),
        });
      });
    },
    handlePopupIndicatorChange() {
      this.refreshPopupSalaryTypeDescriptions();
    },
    applyDefaultConfig(data) {
      const payload = data || {};
      this.ruleConfigurationDesc = payload.ruleConfigurationDesc || DEFAULT_RULE_CONFIGURATION_DESC;
      this.defaultConfigMeta = {
        compareMap: payload.compareMap || {},
        indicatorMap: payload.indicatorMap || {},
        ruleMap: payload.ruleMap || {},
        ruleTypeMap: payload.ruleTypeMap || {},
        configurationList: payload.configurationList || [],
      };
      const nextSections = this.sectionOrder.map((section) => {
        const sectionRuleKey = this.getSectionRuleKey(section.key);
        const sectionConfig = (payload.configurationList || []).find((config) => {
          return config.ruleKey === sectionRuleKey;
        }) || {};
        const ruleMeta = (payload.ruleMap || {})[sectionRuleKey] || {};
        return Object.assign({}, section, {
          title: ruleMeta.ruleName || section.title,
          helpText: ruleMeta.ruleDesc || section.helpText,
          sort: sectionConfig.sort != null ? Number(sectionConfig.sort) : section.sort,
          additiveCategory: sectionConfig.additiveCategory != null
            ? Number(sectionConfig.additiveCategory)
            : section.additiveCategory,
        });
      });
      const sortedSections = this.sortSectionsByService(nextSections);
      this.sectionOrder = sortedSections;
      this.defaultSnapshot = this.serializeSections(sortedSections);
    },
    getFallbackTypeValues(category) {
      if (category === 'basic') {
        return ['fixedBase', 'performance'];
      }
      if (category === 'commission') {
        return ['unit', 'ladder', 'standard'];
      }
      return ['standard', 'ladder', 'unit'];
    },
    getFallbackSchemeOptions(category) {
      if (category === 'basic') {
        return [
          {
            value: 'fixedSalary',
            //value: 'baseSalary',
            label: '底薪',
            indicatorList: [],
            ruleTypeList: ['fixedBase', 'linearDeduction'],
          },
        ];
      }

      if (category === 'commission') {
        return [
          {
            value: 'joinGroup',
            label: '入群人数',
            indicatorList: ['joinGroup'],
            ruleTypeList: ['multiple', 'tiered', 'unifiedStandard'],
          },
          {
            value: 'newPassengerCompleteOrder',
            label: '当日新客完单',
            indicatorList: ['newPassengerCompleteOrder'],
            ruleTypeList: ['multiple', 'tiered', 'unifiedStandard'],
          },
          {
            value: 'hNewPassengerCmpOrd',
            label: '历史新客完单',
            indicatorList: ['hNewPassengerCmpOrd'],
            ruleTypeList: ['multiple', 'tiered', 'unifiedStandard'],
          },
        ];
      }

      return [
        {
          value: 'quitGroupRate',
          label: '退群率',
          indicatorList: ['quitGroupRate'],
          ruleTypeList: ['unifiedStandard', 'tiered', 'multiple'],
        },
      ];
    },
    getPopupTypeOptionsByRuleTypes(ruleTypeList, category) {
      const typeMap = {};
      (ruleTypeList || []).forEach((ruleType) => {
        const planType = this.toPlanTypeByRuleType(ruleType);

        if (
          planType === 'standard'
          && category === 'deduct'
          && typeMap[planType]
          && typeMap[planType].backendRuleType !== 'thresholdBonus'
          && ruleType === 'thresholdBonus'
        ) {
          typeMap[planType] = Object.assign({}, typeMap[planType], {
            backendRuleType: 'thresholdBonus',
          });
          return;
        }

        if (!typeMap[planType]) {
          const base = TYPE_OPTIONS.find(function(option) {
            return option.value === planType;
          });

          if (base) {
            const typeMeta = this.getRuleTypeMeta(ruleType);
            typeMap[planType] = Object.assign({}, base, {
              backendRuleType: ruleType,
              label: typeMeta.name || base.label,
              desc: typeMeta.desc || base.desc,
            });
          }
        }
      });

      const dynamicTypes = Object.keys(typeMap).map(function(key) {
        return typeMap[key];
      });

      if (dynamicTypes.length) {
        return dynamicTypes;
      }

      return this.getFallbackTypeValues(category).map(function(typeValue) {
        const base = TYPE_OPTIONS.find(function(option) {
          return option.value === typeValue;
        });
        return Object.assign({}, base, {
          backendRuleType: PLAN_TYPE_TO_RULE_TYPE[typeValue],
        });
      });
    },
    getPopupIndicatorOptionsByScheme(schemeOption, category) {
      const indicatorList = schemeOption && schemeOption.indicatorList ? schemeOption.indicatorList : [];

      if (indicatorList.length) {
        return indicatorList.map((indicatorKey) => {
          return {
            value: indicatorKey,
            label: this.getIndicatorLabelByKey(indicatorKey, indicatorKey),
          };
        });
      }

      if (category === 'basic') {
        return [{ value: '', label: '无' }];
      }

      return [{ value: '', label: '请选择指标' }];
    },
    buildPopupConfig(category) {
      const sectionRuleKey = this.getSectionRuleKey(category);
      const sectionConfig = (this.defaultConfigMeta.configurationList || []).find((config) => {
        return config.ruleKey === sectionRuleKey;
      });

      const schemeOptions = sectionConfig && sectionConfig.configurationItems
        ? sectionConfig.configurationItems
            .filter(function(item) {
              return item && item.status !== 0;
            })
            .map((item) => {
              return {
                value: item.ruleKey,
                label: this.getRuleNameByKey(item.ruleKey),
                desc: this.getRuleDescByKey(item.ruleKey),
                indicatorList: item.indicatorList || [],
                ruleTypeList: item.ruleTypeList || [],
                ruleTypeConfigMap: item.ruleTypeConfigMap || {},
                settlementValueMin: item.settlementValueMin,
              };
            })
        : [];

      const nextSchemes = schemeOptions.length ? schemeOptions : this.getFallbackSchemeOptions(category);
      return {
        sectionRuleKey: sectionRuleKey,
        schemeOptions: nextSchemes,
      };
    },
    applyPlanDefaultValues(plan, schemeOption) {
      const option = schemeOption || {};
      const indicatorKey = plan.indicatorKey;
      const ruleTypeConfigMap = option.ruleTypeConfigMap || {};
      const indicatorConfig = (indicatorKey && ruleTypeConfigMap[indicatorKey]) || {};
      const allDef = indicatorConfig.allDef || {};
      const compareList = allDef.compareList || [];
      const standardTypeList = allDef.standardTypeList || [];
      const defaultCompare = compareList[0] || '>=';
      const defaultStandardType = standardTypeList[0] || 'preAddUnit';

      if (plan.type === 'fixedBase') {
        plan.amount = '';
      }

      if (plan.type === 'performance') {
        plan.threshold = '';
        plan.reward = '';
        plan.deduction = '';
      }

      if (plan.type === 'standard') {
        plan.compare = this.normalizeCompare(defaultCompare);
        plan.threshold = '';
        plan.amount = '';
      }

      if (plan.type === 'unit') {
        plan.reward = '';
      }

      if (plan.type === 'ladder') {
        plan.tiers = [
          createTier({ start: '', end: '', standardType: defaultStandardType, increment: '', reward: '' }),
        ];
      }

      if (plan.type === 'gate') {
        plan.gates = [
          {
            id: createId(),
            threshold: '',
            reward: '',
          },
        ];
      }

      return plan;
    },
    handlePopupSchemeChange() {
      const schemeOption = this.popup.schemeOptions.find((option) => {
        return option.value === this.popup.form.schemeRuleKey;
      });

      const nextIndicatorOptions = this.getPopupIndicatorOptionsByScheme(schemeOption, this.popup.category);
      const nextSalaryTypes = this.getPopupTypeOptionsByRuleTypes(
        schemeOption ? schemeOption.ruleTypeList : [],
        this.popup.category
      );

      this.popup.indicatorOptions = nextIndicatorOptions;
      this.popup.salaryTypes = nextSalaryTypes;

      if (!nextIndicatorOptions.some((option) => option.value === this.popup.form.indicatorKey)) {
        this.popup.form.indicatorKey = nextIndicatorOptions[0] ? nextIndicatorOptions[0].value : '';
      }

      if (!nextSalaryTypes.some((option) => option.value === this.popup.form.type)) {
        this.popup.form.type = nextSalaryTypes[0] ? nextSalaryTypes[0].value : 'fixedBase';
      }

      this.refreshPopupSalaryTypeDescriptions();
    },
    getTypeLabel(type) {
      const target = TYPE_OPTIONS.find(function(option) {
        return option.value === type;
      });
      return target ? target.label : '方案';
    },
    getPlanDescription(plan) {
      const unitPrice = plan.type === 'performance' ? plan.deduction : plan.reward;
      return this.formatTypeDescription(plan.type, plan.indicatorLabel, plan.unitLabel, unitPrice, plan.additiveCategory)
        || ''.concat(this.getTypeLabel(plan.type), '方案');
    },
    hasDuplicatePlanSelection(sectionKey, ruleKey, indicatorKey, backendRuleType) {
      const section = this.getSection(sectionKey);
      if (!section) {
        return false;
      }

      return section.plans.some(function(plan) {
        return plan.ruleKey === ruleKey
          && (plan.indicatorKey || '') === (indicatorKey || '')
          && plan.backendRuleType === backendRuleType;
      });
    },
    openPlanPopup(category) {
      if (this.isPageReadOnly) {
        return;
      }
      this.trackBuryPoint('openPlanPopup_click', {
        category: category,
        calcDate: this.$route.query.calcDate || '',
        preview: this.isPreviewMode,
      });
      const popupConfig = this.buildPopupConfig(category);
      const schemeOptions = popupConfig.schemeOptions;
      const firstScheme = schemeOptions[0] ? schemeOptions[0].value : '';
      const firstSchemeOption = schemeOptions[0] || null;
      const indicatorOptions = this.getPopupIndicatorOptionsByScheme(firstSchemeOption, category);
      const salaryTypes = this.getPopupTypeOptionsByRuleTypes(
        firstSchemeOption ? firstSchemeOption.ruleTypeList : [],
        category
      );
      const section = this.getSection(category);
      this.popup = {
        visible: true,
        category: category,
        title: ''.concat(section ? section.title : '方案', '添加方案'),
        placeholder: category === 'deduct' ? '请选择考核指标，如当日退群率' : '请选择考核指标，如当日入群人数',
        schemeOptions: schemeOptions,
        indicatorOptions: indicatorOptions,
        salaryTypes: salaryTypes,
        form: {
          schemeRuleKey: firstScheme,
          indicatorKey: indicatorOptions[0] ? indicatorOptions[0].value : '',
          type: salaryTypes[0] ? salaryTypes[0].value : category === 'deduct' ? 'standard' : 'fixedBase',
        },
      };
      this.refreshPopupSalaryTypeDescriptions();
      this.lockPageScroll();
    },
    closePlanPopup() {
      this.trackBuryPoint('closePlanPopup_click', {
        category: this.popup.category || '',
      });
      this.popup.visible = false;
      this.unlockPageScroll();
    },
    lockPageScroll() {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    },
    unlockPageScroll() {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    },
    confirmAddPlan() {
      if (this.isPageReadOnly) {
        return;
      }
      const category = this.popup.category;
      const section = this.getSection(category);
      if (!section) {
        return;
      }

      const selectedScheme = this.popup.schemeOptions.find((option) => {
        return option.value === this.popup.form.schemeRuleKey;
      });
      const selectedType = this.popup.salaryTypes.find((option) => {
        return option.value === this.popup.form.type;
      });
      this.trackBuryPoint('confirmAddPlan_click', {
        category: category,
        schemeRuleKey: this.popup.form.schemeRuleKey,
        indicatorKey: this.popup.form.indicatorKey,
        type: this.popup.form.type,
      });
      const backendRuleType = selectedType
        ? selectedType.backendRuleType
        : PLAN_TYPE_TO_RULE_TYPE[this.popup.form.type];

      if (this.hasDuplicatePlanSelection(category, this.popup.form.schemeRuleKey, this.popup.form.indicatorKey, backendRuleType)) {
        this.showToast('该规则、指标和计薪方式已存在，请重新选择');
        return;
      }

      const indicatorLabel = this.getIndicatorLabelByKey(
        this.popup.form.indicatorKey,
        selectedScheme ? selectedScheme.label : ''
      );
      const nextPlan = createPlan(
        category,
        this.popup.form.type,
        selectedScheme ? selectedScheme.label : this.popup.form.schemeRuleKey,
        indicatorLabel,
        {
          sectionRuleKey: this.getSectionRuleKey(category),
          ruleKey: this.popup.form.schemeRuleKey,
          indicatorKey: this.popup.form.indicatorKey,
          backendRuleType: backendRuleType,
          ruleName: selectedScheme ? selectedScheme.label : '',
          ruleDesc: selectedScheme ? selectedScheme.desc : '',
          indicatorDesc: this.getIndicatorDescByKey(this.popup.form.indicatorKey),
          additiveCategory: this.getSectionAdditiveCategory(category),
          typeName: selectedType ? selectedType.label : this.getTypeLabel(this.popup.form.type),
          typeDesc: selectedType ? selectedType.desc : '',
          unitLabel: this.getIndicatorUnit(this.popup.form.indicatorKey, indicatorLabel),
        }
      );

      this.applyPlanDefaultValues(nextPlan, selectedScheme);

      this.updateSection(category, section.plans.concat(nextPlan));
      this.trackBuryPoint('confirmAddPlan_success', {
        category: category,
        planId: nextPlan.id,
        ruleKey: nextPlan.ruleKey,
        indicatorKey: nextPlan.indicatorKey,
        type: nextPlan.type,
      });
      this.closePlanPopup();
      this.showToast('已添加方案');
    },
    removePlan(sectionKey, planId) {
      if (this.isPageReadOnly) {
        return;
      }
      const section = this.getSection(sectionKey);
      if (!section) {
        return;
      }
      const nextPlans = section.plans.filter(function(plan) {
        return plan.id !== planId;
      });
      this.trackBuryPoint('removePlan_click', {
        sectionKey: sectionKey,
        planId: planId,
      });
      this.updateSection(sectionKey, nextPlans);
      this.showToast('已删除方案');
    },
    addGate(sectionKey, planId) {
      if (this.isPageReadOnly) {
        return;
      }
      const section = this.getSection(sectionKey);
      if (!section) {
        return;
      }
      const nextPlans = section.plans.map(function(plan) {
        if (plan.id !== planId) {
          return plan;
        }
        return Object.assign({}, plan, { gates: plan.gates.concat(createGate()) });
      });
      this.trackBuryPoint('addGate_click', {
        sectionKey: sectionKey,
        planId: planId,
      });
      this.updateSection(sectionKey, nextPlans);
    },
    removeGate(sectionKey, planId, gateIndex) {
      if (this.isPageReadOnly) {
        return;
      }
      const section = this.getSection(sectionKey);
      if (!section) {
        return;
      }
      const nextPlans = section.plans.map(function(plan) {
        if (plan.id !== planId || plan.gates.length === 1) {
          return plan;
        }
        return Object.assign({}, plan, {
          gates: plan.gates.filter(function(_, index) {
            return index !== gateIndex;
          }),
        });
      });
      this.trackBuryPoint('removeGate_click', {
        sectionKey: sectionKey,
        planId: planId,
        gateIndex: gateIndex,
      });
      this.updateSection(sectionKey, nextPlans);
    },
    addTier(sectionKey, planId) {
      if (this.isPageReadOnly) {
        return;
      }
      const section = this.getSection(sectionKey);
      if (!section) {
        return;
      }
      const nextPlans = section.plans.map((plan) => {
        if (plan.id !== planId) {
          return plan;
        }
        const lastTier = plan.tiers[plan.tiers.length - 1];
        if (!lastTier
          || !this.isIntegerValue(lastTier.start)
          || !this.isIntegerValue(lastTier.end)
          || (this.isTierIncrementRequired(lastTier.standardType) && !this.isIntegerValue(lastTier.increment))
          || !this.isMoneyValue(lastTier.reward)
          || Number(lastTier.reward) < 0) {
          this.showToast('请先完成上边阶梯的输入');
          return plan;
        }
        if (Number(lastTier.end) <= Number(lastTier.start)) {
          this.showToast('阶梯结束值必须大于开始值');
          return plan;
        }
        return Object.assign({}, plan, {
          tiers: plan.tiers.concat(createTier({
            start: String(Number(lastTier.end) + 1),
            end: '',
            standardType: lastTier.standardType || 'preAddUnit',
            increment: lastTier.increment || '1',
            reward: '',
          })),
        });
      });
      this.trackBuryPoint('addTier_click', {
        sectionKey: sectionKey,
        planId: planId,
      });
      this.updateSection(sectionKey, nextPlans);
    },
    removeTier(sectionKey, planId, tierIndex) {
      if (this.isPageReadOnly) {
        return;
      }
      const section = this.getSection(sectionKey);
      if (!section) {
        return;
      }
      const nextPlans = section.plans.map(function(plan) {
        if (plan.id !== planId || plan.tiers.length === 1) {
          return plan;
        }
        const filteredTiers = plan.tiers.filter(function(_, index) {
          return index !== tierIndex;
        });
        return Object.assign({}, plan, {
          tiers: this.normalizeTierRanges(filteredTiers),
        });
      }, this);
      this.trackBuryPoint('removeTier_click', {
        sectionKey: sectionKey,
        planId: planId,
        tierIndex: tierIndex,
      });
      this.updateSection(sectionKey, nextPlans);
    },
    resetRules() {
      if (this.isPageReadOnly) {
        return;
      }
      this.trackBuryPoint('resetRules_click', {
        groupId: this.$route.query.groupId || '',
      });
      this.sectionOrder = this.createEmptySections();
      this.showToast('已重置');
    },
    loadPageData() {
      const groupId = Number(this.$route.query.groupId || 0);
      const requestId = Date.now() + Math.random();
      this.latestLoadRequestId = requestId;
      const baseSections = this.sortSectionsByService(createInitialSections());
      this.activeHelpSectionKey = '';
      this.ruleConfigurationDesc = DEFAULT_RULE_CONFIGURATION_DESC;
      this.defaultConfigMeta = {
        compareMap: {},
        indicatorMap: {},
        ruleMap: {},
        ruleTypeMap: {},
        configurationList: [],
      };
      this.sectionOrder = baseSections;
      this.defaultSnapshot = this.serializeSections(baseSections);

      if (!groupId) {
        this.trackBuryPoint('loadPageData_skip', {
          groupId: groupId,
        });
        return;
      }

      const defaultConfigParams = { groupId: groupId };
      this.trackBuryPoint('reqSalaryTemplateDefaultConfig_params', defaultConfigParams);
      reqSalaryTemplateDefaultConfig(defaultConfigParams)
        .then((res) => {
          if (!this.isAlive || this.latestLoadRequestId !== requestId) {
            return;
          }
          if (res && res.code === 1) {
            this.trackBuryPoint('reqSalaryTemplateDefaultConfig_success', {
              groupId: groupId,
              sectionCount: (res.data && res.data.configurationList && res.data.configurationList.length) || 0,
            });
            this.applyDefaultConfig(res.data || {});
            this.fetchGroupDetail(requestId);
            return;
          }
          this.trackBuryPoint('reqSalaryTemplateDefaultConfig_err', {
            groupId: groupId,
            code: res && res.code,
            msg: res && res.msg,
          });
          this.showToast('获取配置项目失败');
          this.fetchGroupDetail(requestId);
        })
        .catch((error) => {
          if (!this.isAlive || this.latestLoadRequestId !== requestId) {
            return;
          }
          this.trackBuryPoint('reqSalaryTemplateDefaultConfig_catch', {
            groupId: groupId,
            errorMessage: error && error.message ? error.message : '',
          });
          this.showToast('获取配置项目异常');
          this.fetchGroupDetail(requestId);
        });
    },
    fetchGroupDetail(requestId) {
      const groupId = Number(this.$route.query.groupId || 0);
      const cityCode = this.$route.query.cityCode || '110100';
      const requestPayload = {
        groupId: groupId,
        cityCode: cityCode,
      };

      if (this.shouldUsePreviewCalcDate) {
        requestPayload.calcDate = this.previewCalcDate;
      }

      this.trackBuryPoint('reqSalaryTemplateGroupDetail_params', requestPayload);
      reqSalaryTemplateGroupDetail(requestPayload)
        .then((res) => {
          if (!this.isAlive || this.latestLoadRequestId !== requestId) {
            return;
          }
          if (res && res.code === 1) {
            this.hydrateSections(res.data && res.data.ruleConfigList ? res.data.ruleConfigList : []);
            this.trackBuryPoint('reqSalaryTemplateGroupDetail_success', {
              groupId: groupId,
              cityCode: cityCode,
              ruleConfigCount: (res.data && res.data.ruleConfigList && res.data.ruleConfigList.length) || 0,
            });
            return;
          }
          this.trackBuryPoint('reqSalaryTemplateGroupDetail_err', {
            groupId: groupId,
            cityCode: cityCode,
            code: res && res.code,
            msg: res && res.msg,
          });
          this.showToast('获取团队规则失败');
        })
        .catch((error) => {
          if (!this.isAlive || this.latestLoadRequestId !== requestId) {
            return;
          }
          this.trackBuryPoint('reqSalaryTemplateGroupDetail_catch', {
            groupId: groupId,
            cityCode: cityCode,
            errorMessage: error && error.message ? error.message : '',
          });
          this.showToast('获取团队规则异常');
        });
    },
    hydrateSections(ruleConfigList) {
      const nextSections = this.restoreSections(this.defaultSnapshot).map(function(section) {
        return Object.assign({}, section, { plans: [] });
      });
      const sectionMap = nextSections.reduce((result, section) => {
        result[section.key] = section;
        return result;
      }, {});

      const sortedRuleConfigList = (ruleConfigList || []).slice().sort((left, right) => {
        const leftSectionKey = this.getSectionKeyByRuleKey(left.ruleKey);
        const rightSectionKey = this.getSectionKeyByRuleKey(right.ruleKey);
        const leftSort = left.sort != null ? Number(left.sort) : (sectionMap[leftSectionKey] && sectionMap[leftSectionKey].sort) || 99;
        const rightSort = right.sort != null ? Number(right.sort) : (sectionMap[rightSectionKey] && sectionMap[rightSectionKey].sort) || 99;
        if (leftSort !== rightSort) {
          return leftSort - rightSort;
        }
        return leftSectionKey.localeCompare(rightSectionKey);
      });

      sortedRuleConfigList.forEach((config) => {
        const sectionKey = this.getSectionKeyByRuleKey(config.ruleKey);
        const sectionTarget = sectionMap[sectionKey];

        if (!sectionTarget) {
          return;
        }

        sectionTarget.sort = config.sort != null ? Number(config.sort) : sectionTarget.sort;
        sectionTarget.additiveCategory = config.additiveCategory != null
          ? Number(config.additiveCategory)
          : sectionTarget.additiveCategory;

        (config.ruleDetailList || []).forEach((detail) => {
          const planType = this.toPlanTypeByRuleType(detail.ruleType);
          const schemeName = this.getRuleNameByKey(detail.ruleKey);
          const indicatorLabel = this.getIndicatorLabelByKey(detail.indicatorKey, schemeName);
          const plan = createPlan(
            sectionKey,
            planType,
            schemeName,
            indicatorLabel,
            {
              sectionRuleKey: config.ruleKey,
              ruleKey: detail.ruleKey,
              indicatorKey: detail.indicatorKey || '',
              backendRuleType: detail.ruleType,
              ruleName: schemeName,
              ruleDesc: this.getRuleDescByKey(detail.ruleKey),
              indicatorDesc: this.getIndicatorDescByKey(detail.indicatorKey),
              additiveCategory: sectionTarget.additiveCategory,
              sort: config.sort,
              typeName: this.getRuleTypeMeta(detail.ruleType).name,
              typeDesc: this.getRuleTypeMeta(detail.ruleType).desc,
              unitLabel: this.getIndicatorUnit(detail.indicatorKey, indicatorLabel),
            }
          );

          if (planType === 'fixedBase') {
            plan.amount = this.normalizeDisplaySettlementValue(detail.settlementValue);
          }

          if (planType === 'performance') {
            plan.threshold = detail.standardValue != null ? String(detail.standardValue) : '';
            plan.reward = this.normalizeDisplaySettlementValue(detail.settlementValue);
            plan.deduction = detail.deduction != null ? String(detail.deduction) : '';
          }

          if (planType === 'standard') {
            plan.compare = this.normalizeCompare(detail.compare);
            plan.threshold = detail.standardValue != null ? String(detail.standardValue) : '';
            plan.amount = this.normalizeDisplaySettlementValue(detail.settlementValue);
          }

          if (planType === 'unit') {
            plan.reward = this.normalizeDisplaySettlementValue(detail.settlementValue);
          }

          if (planType === 'ladder') {
            plan.tiers = (detail.tieredList || []).length
              ? detail.tieredList
                  .slice()
                  .sort((left, right) => {
                    const leftLevel = left.level != null ? Number(left.level) : Number(left.start || 0);
                    const rightLevel = right.level != null ? Number(right.level) : Number(right.start || 0);
                    return leftLevel - rightLevel;
                  })
                  .map((tier) => {
                  return createTier({
                    level: tier.level,
                    start: tier.start != null ? String(tier.start) : '',
                    end: tier.end != null ? String(tier.end) : '',
                    standardType: tier.standardType || 'preAddUnit',
                    increment: tier.standardValue != null ? String(tier.standardValue) : '',
                    reward: this.normalizeDisplaySettlementValue(tier.settlementValue),
                  });
                })
              : [];
          }

          if (planType === 'gate') {
            plan.gates = (detail.tieredList || []).length
              ? detail.tieredList
                  .slice()
                  .sort((left, right) => {
                    const leftLevel = left.level != null ? Number(left.level) : Number(left.standardValue || 0);
                    const rightLevel = right.level != null ? Number(right.level) : Number(right.standardValue || 0);
                    return leftLevel - rightLevel;
                  })
                  .map((tier) => {
                    return createGate({
                      level: tier.level,
                      threshold: tier.standardValue != null ? String(tier.standardValue) : '',
                      reward: this.normalizeDisplaySettlementValue(tier.settlementValue),
                    });
                  })
              : (detail.standardValue != null || detail.settlementValue != null)
                ? [createGate({
                    threshold: detail.standardValue != null ? String(detail.standardValue) : '',
                    reward: this.normalizeDisplaySettlementValue(detail.settlementValue),
                  })]
                : [];
          }

          sectionTarget.plans.push(plan);
        });
      });

      const sortedSections = this.sortSectionsByService(nextSections);
      this.sectionOrder = sortedSections;
      this.defaultSnapshot = this.serializeSections(sortedSections);
    },
    normalizeCompare(compare) {
      if (compare === '>=') {
        return 'gte';
      }
      if (compare === '=') {
        return 'eq';
      }
      if (compare === '<') {
        return 'lt';
      }
      if (compare === '<=') {
        return 'lte';
      }
      return 'gt';
    },
    toApiCompare(compare) {
      const compareMap = {
        gt: '>',
        gte: '>=',
        eq: '=',
        lt: '<',
        lte: '<=',
      };
      return compareMap[compare] || '>';
    },
    getCompareLabel(compareSymbol) {
      const target = this.defaultConfigMeta.compareMap[compareSymbol] || {};
      return target.desc || compareSymbol;
    },
    getUnsupportedPlanMessage() {
      return '';
    },
    isNumericValue(value) {
      if (value === '' || value === null || value === undefined) {
        return false;
      }
      return !Number.isNaN(Number(value));
    },
    getInvalidFieldMessage() {
      for (let index = 0; index < this.sectionOrder.length; index += 1) {
        const section = this.sectionOrder[index];

        for (let planIndex = 0; planIndex < section.plans.length; planIndex += 1) {
          const plan = section.plans[planIndex];

          if (plan.type === 'fixedBase' && !this.isNumericValue(plan.amount)) {
            return ''.concat(section.title, '的', plan.title, '请填写有效金额');
          }

          if (plan.type === 'fixedBase' && !this.isMoneyValue(plan.amount)) {
            return ''.concat(section.title, '的', plan.title, '金额最多保留两位小数');
          }

          if (plan.type === 'fixedBase' && Number(plan.amount) < 0) {
            return ''.concat(section.title, '的', plan.title, '金额不能小于0');
          }

          if (plan.type === 'performance') {
            if (!plan.indicatorKey) {
              return ''.concat(section.title, '的', plan.title, '请选择指标');
            }

            if (!this.isIntegerValue(plan.threshold) || !this.isMoneyValue(plan.reward) || !this.isMoneyValue(plan.deduction)) {
              return ''.concat(section.title, '的', plan.title, '请填写完整数值');
            }

            if (Number(plan.reward) < 0) {
              return ''.concat(section.title, '的', plan.title, '奖励需大于等于0');
            }

            if (!this.isWithinStandardInputRange(plan, plan.threshold)) {
              return ''.concat(section.title, '的', plan.title, this.getStandardInputRangeMessage(plan, '门槛值'));
            }

            if (Number(plan.deduction) < 0) {
              return ''.concat(section.title, '的', plan.title, '扣减值需大于等于0');
            }
          }

          if (plan.type === 'unit' && !plan.indicatorKey) {
            return ''.concat(section.title, '的', plan.title, '请选择指标');
          }

          if (plan.type === 'unit' && !this.isMoneyValue(plan.reward)) {
            return ''.concat(section.title, '的', plan.title, '请填写有效金额');
          }

          if (plan.type === 'unit' && Number(plan.reward) < 0) {
            return ''.concat(section.title, '的', plan.title, '金额不能小于0');
          }

          if (plan.type === 'ladder') {
            if (!plan.indicatorKey) {
              return ''.concat(section.title, '的', plan.title, '请选择指标');
            }

            for (let tierIndex = 0; tierIndex < plan.tiers.length; tierIndex += 1) {
              const tier = plan.tiers[tierIndex];
              if (!this.isIntegerValue(tier.start) || (this.isTierIncrementRequired(tier.standardType) && !this.isIntegerValue(tier.increment)) || !this.isMoneyValue(tier.reward)) {
                return ''.concat(section.title, '的', plan.title, '请填写完整阶梯数值');
              }

              if (tier.end !== '' && !this.isIntegerValue(tier.end)) {
                return ''.concat(section.title, '的', plan.title, '阶梯结束值必须是整数');
              }

              if (tier.end !== '' && Number(tier.end) <= Number(tier.start)) {
                return ''.concat(section.title, '的', plan.title, '阶梯结束值必须大于开始值');
              }

              if (!this.isWithinStandardInputRange(plan, tier.start)) {
                return ''.concat(section.title, '的', plan.title, this.getStandardInputRangeMessage(plan, '阶梯开始值'));
              }

              if (tier.end !== '' && !this.isWithinStandardInputRange(plan, tier.end)) {
                return ''.concat(section.title, '的', plan.title, this.getStandardInputRangeMessage(plan, '阶梯结束值'));
              }

              if (this.isTierIncrementRequired(tier.standardType) && !this.isWithinStandardInputRange(plan, tier.increment)) {
                return ''.concat(section.title, '的', plan.title, this.getStandardInputRangeMessage(plan, '阶梯增量值'));
              }

              if (tierIndex > 0) {
                const previousTier = plan.tiers[tierIndex - 1];
                if (!this.isIntegerValue(previousTier.end) || Number(tier.start) !== Number(previousTier.end) + 1) {
                  return ''.concat(section.title, '的', plan.title, '阶梯区间必须连续');
                }
              }

              if (Number(tier.reward) < 0) {
                return ''.concat(section.title, '的', plan.title, '阶梯奖励需大于等于0');
              }
            }
          }

          if (plan.type === 'gate') {
            if (!plan.indicatorKey) {
              return ''.concat(section.title, '的', plan.title, '请选择指标');
            }

            for (let gateIndex = 0; gateIndex < plan.gates.length; gateIndex += 1) {
              const gate = plan.gates[gateIndex];
              if (!this.isIntegerValue(gate.threshold) || !this.isMoneyValue(gate.reward)) {
                return ''.concat(section.title, '的', plan.title, '请填写完整门槛数值');
              }

              const thresholdValidation = this.validateGateThreshold(plan, gateIndex, gate.threshold);
              if (!thresholdValidation.valid) {
                return ''.concat(section.title, '的', plan.title, '门槛值必须保持递增');
              }

              if (!this.isWithinStandardInputRange(plan, gate.threshold)) {
                return ''.concat(section.title, '的', plan.title, this.getStandardInputRangeMessage(plan, '门槛值'));
              }

              if (Number(gate.reward) < 0) {
                return ''.concat(section.title, '的', plan.title, '门槛数值需大于等于0');
              }
            }
          }

          if (plan.type === 'standard') {
            if (!plan.indicatorKey) {
              return ''.concat(section.title, '的', plan.title, '请选择指标');
            }

            if (!plan.compare || !this.isIntegerValue(plan.threshold) || !this.isMoneyValue(plan.amount)) {
              return ''.concat(section.title, '的', plan.title, '请填写完整数值');
            }

            if (!this.getCompareOptions(plan).some((option) => option.value === plan.compare)) {
              return ''.concat(section.title, '的', plan.title, '比较方式不符合当前规则配置');
            }

            if (!this.isWithinStandardInputRange(plan, plan.threshold)) {
              return ''.concat(section.title, '的', plan.title, this.getStandardInputRangeMessage(plan, '标准值'));
            }

            if (Number(plan.amount) < 0) {
              return ''.concat(section.title, '的', plan.title, '金额需大于等于0');
            }
          }
        }
      }

      return '';
    },
    buildRulePayload() {
      const ruleConfigList = [];

      this.sectionOrder.forEach((section, index) => {
        const detailList = (section.plans || [])
          .map((plan) => {
            const ruleKey = plan.ruleKey || '';
            const indicatorKey = plan.indicatorKey || '';
            const additiveCategory = plan.additiveCategory != null
              ? Number(plan.additiveCategory)
              : this.getSectionAdditiveCategory(section.key);

            if (plan.type === 'fixedBase') {
              return {
                ruleKey: ruleKey,
                ruleType: plan.backendRuleType || 'fixedBase',
                settlementValue: this.toPayloadSettlementValue(plan.amount, additiveCategory),
              };
            }

            if (plan.type === 'performance') {
              const detail = {
                ruleKey: ruleKey,
                ruleType: plan.backendRuleType || 'linearDeduction',
                standardValue: Number(plan.threshold || 0),
                settlementValue: this.toPayloadSettlementValue(plan.reward, additiveCategory),
                deduction: Number(plan.deduction || 0),
              };
              if (indicatorKey) {
                detail.indicatorKey = indicatorKey;
              }
              return detail;
            }

            if (plan.type === 'standard') {
              const detail = {
                ruleKey: ruleKey,
                ruleType: plan.backendRuleType || 'unifiedStandard',
                compare: this.toApiCompare(plan.compare),
                standardValue: Number(plan.threshold || 0),
                settlementValue: this.toPayloadSettlementValue(plan.amount, additiveCategory),
              };
              if (indicatorKey) {
                detail.indicatorKey = indicatorKey;
              }
              return detail;
            }

            if (plan.type === 'unit') {
              const detail = {
                ruleKey: ruleKey,
                ruleType: plan.backendRuleType || 'multiple',
                settlementValue: this.toPayloadSettlementValue(plan.reward, additiveCategory),
              };
              if (indicatorKey) {
                detail.indicatorKey = indicatorKey;
              }
              return detail;
            }

            if (plan.type === 'gate' || plan.type === 'ladder') {
              const sourceList = plan.type === 'gate' ? plan.gates : plan.tiers;
              const detail = {
                ruleKey: ruleKey,
                ruleType: plan.backendRuleType || (plan.type === 'gate' ? 'thresholdBonus' : 'tiered'),
                tieredList: (sourceList || []).map(function(item, tierIndex) {
                  if (plan.type === 'gate') {
                    return {
                      level: item.level != null ? Number(item.level) : tierIndex + 1,
                      standardValue: Number(item.threshold || 0),
                      //settlementValue: Number(additiveCategory) === 2 ? -Math.abs(Number(item.reward || 0)) : Math.abs(Number(item.reward || 0)),
                      settlementValue: Number(Math.abs(Number(item.reward || 0))),
                    };
                  }

                  return {
                    start: Number(item.start || 0),
                    end: item.end === '' || item.end == null ? null : Number(item.end),
                    standardType: item.standardType || 'preAddUnit',
                    level: item.level != null ? Number(item.level) : tierIndex + 1,
                    standardValue: ['perUnit', 'preUnit', 'perPerson'].includes(item.standardType)
                      ? null
                      : Number(item.increment || 0),
                    //settlementValue: Number(additiveCategory) === 2 ? -Math.abs(Number(item.reward || 0)) : Math.abs(Number(item.reward || 0)),
                    settlementValue: Number(Math.abs(Number(item.reward || 0))),
                  };
                }),
              };
              if (indicatorKey) {
                detail.indicatorKey = indicatorKey;
              }
              return detail;
            }

            return null;
          })
          .filter(Boolean);

        if (!detailList.length) {
          return;
        }

        ruleConfigList.push({
          ruleKey: this.getSectionRuleKey(section.key),
          sort: section.sort != null ? Number(section.sort) : index + 1,
          additiveCategory: this.getSectionAdditiveCategory(section.key),
          ruleDetailList: detailList,
        });
      });

      return ruleConfigList;
    },
    persistTemplate(successMessage, options) {
      const setting = options || {};
      const groupId = Number(this.$route.query.groupId);
      const cityCode = this.$route.query.cityCode;
      const saveRouteKey = this.getCurrentLoadKey();
      const saveToken = Date.now() + Math.random();

      if (!groupId) {
        this.showToast('当前无团队参数，暂未提交后台');
        return;
      }

      if (this.isSavingCurrentRoute) {
        return;
      }

      this.isSubmitting = true;
      this.activeSaveToken = saveToken;
      this.submittingRouteKey = saveRouteKey;
      const params = {
        groupId: groupId,
        cityCode: cityCode,
        ruleConfigList: this.buildRulePayload(),
      };
      this.trackBuryPoint('reqSalaryTemplateOperation_params', params);
      reqSalaryTemplateOperation(params)
        .then((res) => {
          if (!this.isAlive || this.activeSaveToken !== saveToken) {
            return;
          }
          if (res && res.code === 1) {
            this.trackBuryPoint('reqSalaryTemplateOperation_success', {
              groupId: groupId,
              cityCode: cityCode,
              ruleConfigCount: params.ruleConfigList.length,
            });
            if (this.getCurrentLoadKey() === saveRouteKey) {
              this.showToast(successMessage || '保存成功');
            }
            if (setting.closePopupOnSuccess) {
              this.closePlanPopup();
            }
            if (this.getCurrentLoadKey() === saveRouteKey) {
              this.loadPageData();
            }
            return;
          }

          this.trackBuryPoint('reqSalaryTemplateOperation_err', {
            groupId: groupId,
            cityCode: cityCode,
            code: res && res.code,
            msg: res && res.msg,
          });

          if (setting.rollbackSnapshot && this.getCurrentLoadKey() === saveRouteKey) {
            this.sectionOrder = this.restoreSections(setting.rollbackSnapshot);
          }
          if (this.getCurrentLoadKey() === saveRouteKey) {
            this.showToast(res.msg || '保存失败');
          }
        })
        .catch((error) => {
          if (!this.isAlive || this.activeSaveToken !== saveToken) {
            return;
          }
          this.trackBuryPoint('reqSalaryTemplateOperation_catch', {
            groupId: groupId,
            cityCode: cityCode,
            errorMessage: error && error.message ? error.message : '',
          });
          if (setting.rollbackSnapshot && this.getCurrentLoadKey() === saveRouteKey) {
            this.sectionOrder = this.restoreSections(setting.rollbackSnapshot);
          }
          if (this.getCurrentLoadKey() === saveRouteKey) {
            this.showToast('保存失败');
          }
        })
        .finally(() => {
          if (this.activeSaveToken === saveToken) {
            this.isSubmitting = false;
            this.activeSaveToken = null;
            this.submittingRouteKey = '';
          }
        });
    },
    saveRules() {
      if (this.isPreviewMode) {
        return;
      }
      this.trackBuryPoint('saveRules_click', {
        groupId: this.$route.query.groupId || '',
        cityCode: this.$route.query.cityCode || '',
        sectionCount: this.sectionOrder.length,
      });
      const basicSection = this.getSection('basic');

      if (!basicSection || !basicSection.plans.length) {
        this.trackBuryPoint('saveRules_blocked', {
          reason: 'missingBasicSection',
          groupId: this.$route.query.groupId || '',
        });
        this.showToast('请至少配置一项基础薪资');
        return;
      }

      const unsupportedMessage = this.getUnsupportedPlanMessage();
      if (unsupportedMessage) {
        this.trackBuryPoint('saveRules_blocked', {
          reason: 'unsupportedPlan',
          message: unsupportedMessage,
        });
        this.showToast(unsupportedMessage);
        return;
      }

      const invalidFieldMessage = this.getInvalidFieldMessage();
      if (invalidFieldMessage) {
        this.trackBuryPoint('saveRules_blocked', {
          reason: 'invalidField',
          message: invalidFieldMessage,
        });
        this.showToast(invalidFieldMessage);
        return;
      }

      this.persistTemplate('保存成功');
    },
  },
};
</script>

<style scoped>
.commission-page {
  min-height: 100vh;
  background: #f7f8fa;
  padding: 34px 32px 208px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue',
    Arial, sans-serif;
  color: #1c2742;
}

.page-content {
  max-width: 880px;
  margin: 0 auto;
  padding-bottom: 500px;
}

.page-meta {
  display: flex;
  align-items: center;
  gap: 44px;
  font-size: 26px;
  font-weight: 600;
  line-height: 40px;
  color: #373737;
  margin-bottom: 34px;
}

.hero-card,
.plan-card,
.empty-card {
  background: #ffffff;
  border-radius: 34px;
  box-shadow: 0 4px 18px rgba(24, 45, 87, 0.08), 0 1px 3px rgba(24, 45, 87, 0.05);
}

.hero-card {
  padding: 42px 48px 36px;
  margin-bottom: 34px;
}

.hero-title {
  margin: 0;
  font-size: 38px;
  line-height: 1.18;
  font-weight: 700;
  color: #1a2540;
}

.hero-desc {
  margin: 26px 0 0;
  font-size: 26px;
  line-height: 1.85;
  color: #6e82a7;
}

.salary-section {
  margin-bottom: 40px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.section-title-wrap {
  display: flex;
  align-items: center;
  gap: 18px;
}

.section-heading {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.section-bar {
  width: 16px;
  height: 48px;
  border-radius: 999px;
}

.bar-basic {
  background: linear-gradient(180deg, #1474d5 0%, #0f88ea 100%);
}

.bar-commission {
  background: linear-gradient(180deg, #14cfa0 0%, #0ab375 100%);
}

.bar-deduct {
  background: linear-gradient(180deg, #ff296a 0%, #ff1854 100%);
}

.section-title {
  margin: 0;
  font-size: 38px;
  line-height: 1.2;
  font-weight: 700;
  color: #18233e;
}

.section-subtitle {
  margin: 0;
  font-size: 26px;
  line-height: 1.2;
  font-weight: 500;
  color: #6f7c94;
}

.icon-btn {
  border: none;
  background: transparent;
  padding: 0;
}

.help-btn {
  width: 20px;
  height: 20px;
  color: #747b8a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

.add-plan-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 206px;
  height: 64px;
  border: none;
  border-radius: 22px;
  font-size: 26px;
  font-weight: 600;
  padding: 0 26px;
}

.btn-basic {
  background: #e9f3ff;
  color: #0f65bb;
}

.btn-commission {
  background: #eafcf5;
  color: #0fa66f;
}

.btn-deduct {
  background: #ffeef2;
  color: #ff1854;
}

.plans-list {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.plan-card {
  position: relative;
  padding: 34px 28px 30px;
}

.delete-plan-btn,
.inline-delete-btn {
  border: none;
  background: transparent;
  color: #d1d9e6;
  padding: 0;
}

.delete-plan-btn {
  position: absolute;
  top: 30px;
  right: 26px;
  font-size: 26px;
}

.plan-header-main {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-right: 34px;
}

.plan-title {
  margin: 0;
  font-size: 26px;
  line-height: 1.2;
  font-weight: 700;
  color: #1b263f;
}

.plan-tag {
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 16px;
  border-radius: 999px;
  background: #eef2f7;
  color: #5f6f88;
  font-size: 26px;
  font-weight: 600;
}

.plan-desc {
  margin: 16px 0 0;
  font-size: 26px;
  line-height: 1.75;
  color: #7183a3;
}

.plan-body {
  margin-top: 22px;
}

.form-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.compact-row {
  margin-top: 0;
}

.no-margin-top {
  margin-top: 12px;
}

.field-label,
.field-text,
.field-unit {
  font-size: 26px;
  line-height: 38px;
  color: #37435d;
  font-weight: 600;
}

.field-unit {
  white-space: nowrap;
}

.field-inline-group {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  white-space: nowrap;
}

.text-input,
.select-input,
.popup-select,
.popup-input {
  height: 54px;
  border-radius: 14px;
  border: 1px solid #dce5f1;
  background: #ffffff;
  padding: 0 16px;
  font-size: 26px;
  color: #1f2840;
  outline: none;
  box-sizing: border-box;
}

.text-input::placeholder,
.popup-input::placeholder {
  color: #c1cad8;
}

.text-input {
  min-width: 0;
  flex: 1;
  text-align: center;
}

.text-input::after {
  display: none;
}

::v-deep .text-input .van-field__body {
  height: 100%;
}

::v-deep .text-input .van-field__control {
  text-align: center;
  font-size: 26px;
  color: #1f2840;
}

::v-deep .text-input .van-field__control::placeholder {
  color: #c1cad8;
}

.short-input {
  width: 140px;
  flex: 0 0 140px;
}

.tiny-input {
  width: 140px;
  flex: 0 0 140px;
  text-align: center;
}

.readonly-input {
  background: #f4f7fb;
  color: #8b97ab;
}

.text-input[readonly],
.select-input:disabled {
  background: #f4f7fb;
  color: #8b97ab;
  cursor: not-allowed;
}

::v-deep .text-input .van-field__control[readonly] {
  color: #8b97ab;
}

.small-select {
  width: 180px;
  flex: 0 0 180px;
}

.gate-row,
.tier-row,
.standard-row {
  align-items: center;
}

.inline-delete-btn {
  font-size: 26px;
}

.push-right {
  margin-left: auto;
}

.tier-block {
  margin-bottom: 14px;
}

.add-tier-btn {
  width: 100%;
  height: 58px;
  border-radius: 16px;
  border: 1px dashed #c8d7ea;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #344765;
  font-size: 26px;
  font-weight: 600;
}

.empty-card {
  min-height: 224px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 600;
  color: #6f85ac;
}

.bottom-actions {
  position: fixed;
  left: 50%;
  bottom: 38px;
  transform: translateX(-50%);
  width: calc(100% - 64px);
  max-width: 880px;
  display: grid;
  grid-template-columns: 1fr 262px;
  gap: 36px;
}

.action-btn {
  height: 94px;
  border-radius: 4px;
  font-size: 26px;
  font-weight: 600;
}

.primary-btn {
  border: none;
  background: linear-gradient(90deg, #4168ef 0%, #4160ea 100%);
  color: #ffffff;
}

.ghost-btn {
  border: 2px solid #4068ef;
  background: #ffffff;
  color: #4068ef;
}

.popup-mask {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
  box-sizing: border-box;
}

.popup-panel {
  width: 80vw;
  height: 80vh;
  max-width: 880px;
  max-height: 80vh;
  background: #ffffff;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
}

.popup-body {
  flex: 1;
  min-height: 0;
  padding: 44px 38px 24px;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
}

.popup-title {
  text-align: center;
  font-size: 38px;
  line-height: 1.2;
  font-weight: 700;
  color: #19233d;
  margin-bottom: 44px;
}

.popup-section {
  margin-bottom: 48px;
}

.popup-label {
  display: block;
  font-size: 26px;
  line-height: 40px;
  font-weight: 700;
  color: #16213c;
  margin-bottom: 20px;
}

.select-wrapper {
  position: relative;
}

.popup-select,
.popup-input {
  width: 100%;
  height: 80px;
  border-radius: 18px;
  font-size: 26px;
}

.radio-list {
  display: flex;
  flex-direction: column;
  gap: 26px;
}

.radio-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  font-size: 26px;
  line-height: 1.55;
  color: #3d4559;
}

.radio-item input {
  display: none;
}

.radio-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #8eb0ff;
  margin-top: 2px;
  position: relative;
  flex: 0 0 28px;
}

.radio-item input:checked + .radio-dot {
  border-color: #4366eb;
}

.radio-item input:checked + .radio-dot::after {
  content: '';
  position: absolute;
  inset: 6px;
  border-radius: 50%;
  background: #4366eb;
}

.radio-text {
  font-size: 26px;
  line-height: 1.6;
  color: #3b445c;
}

.popup-footer {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  padding: 0 38px 20px;
  background: #ffffff;
  box-sizing: border-box;
}

.popup-cancel,
.popup-submit {
  height: 84px;
  border: none;
  border-radius: 6px;
  font-size: 26px;
  font-weight: 600;
}

.popup-submit {
  background: linear-gradient(90deg, #4168ef 0%, #415fe9 100%);
  color: #ffffff;
}

.popup-cancel {
  background: #edf1f7;
  color: #5c6880;
}

.help-popover-content {
  max-width: 320px;
  font-size: 26px;
  padding: 10px;
  line-height: 1.6;
  color: #fff;
  white-space: normal;
  background-color: #000;
}

</style>
